import { useMemo, useState, useEffect } from "react";
import ThemeSwitcher from "../components/ThemeSwitcher";
import { getDashboard } from "../store/slices/auth";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

export const bodyClass =
  "bg-slate-950 text-slate-100 min-h-screen flex flex-col font-sans selection:bg-blue-600 selection:text-white";

const initialForm = {
  name: "",
  subDivisionId: "",
  dspOfficerId: "",
  dspName: "",
  phone: "",
  lat: "",
  lng: "",
  active: true,
};

export default function SubDivisionsMarkup({
  subdivisions = [],
  totalSubDivisions = 0,
  statistics = {},
  loading = false,
  error = null,
  detailsLoading = false,
  saving = false,
  search,
  setSearch,
  status,
  setStatus,
  onAddSubdivision,
  onGetSubdivision,
  onCreateSubdivision,
  onUpdateSubdivision,

  dspUsers = [],
  usersLoading = false,
  onGetUsers,
}) {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    user,
    dashboard,
    dashboardLoading,
    dashboardError
  } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getDashboard());
  }, [dispatch]);

  const handleLogout = () => {
    const confirmed = window.confirm(
      "Are you sure you want to logout?"
    );

    if (!confirmed) return;

    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("admin");

    sessionStorage.clear();

    if (window.adminSocket) {
      window.adminSocket.disconnect();
      window.adminSocket = null;
    }

    window.location.replace("/login");
  };

  const [viewMode, setViewMode] = useState("GRID");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [formError, setFormError] = useState("");



  const formatCoordinates = (lat, lng) => {
    if (lat == null || lng == null) return "--";
    return `${lat}, ${lng}`;
  };

  const updateField = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };
  const openAddModal = async () => {
    onAddSubdivision?.();

    setEditingId(null);
    setForm(initialForm);
    setFormError("");
    setShowModal(true);

    try {
      await onGetUsers?.();
    } catch (err) {
      setFormError(err?.message || "Unable to fetch DSP users.");
    }
  };
  const closeModal = () => {
    if (saving) return;

    setShowModal(false);
    setEditingId(null);
    setForm(initialForm);
    setFormError("");
  };

  const openEditModal = async (id) => {
    setFormError("");
    setEditingId(id);
    setShowModal(true);
    setForm(initialForm);

    try {
      const [, subdivision] = await Promise.all([
        onGetUsers?.(),
        onGetSubdivision?.(id),
      ]);

      if (!subdivision) {
        throw new Error("Subdivision details not found.");
      }

      const assignedDsp =
        subdivision.dspOfficerId?._id ||
        subdivision.dspOfficerId ||
        "";

      setForm({
        name: subdivision.name || "",
        subDivisionId: subdivision.subDivisionId || "",
        dspOfficerId: assignedDsp,
        dspName:
          subdivision.dspOfficerId?.displayName ||
          subdivision.dspOfficerId?.username ||
          subdivision.dspName ||
          "",
        phone: subdivision.phone || "",
        lat: subdivision.lat ?? "",
        lng: subdivision.lng ?? "",
        active: subdivision.active !== false,
      });
    } catch (err) {
      setFormError(
        err?.message || "Unable to fetch subdivision details."
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.name.trim()) {
      setFormError("Please enter the sub-division name.");
      return;
    }
    if (!form.subDivisionId.trim()) {
      setFormError("Please enter the sub-division ID.");
      return;
    }

    if (form.phone.trim() && !/^[0-9+\-\s]{7,15}$/.test(form.phone.trim())) {
      setFormError("Please enter a valid phone number.");
      return;
    }

    if (
      form.lat !== "" &&
      (Number.isNaN(Number(form.lat)) ||
        Number(form.lat) < -90 ||
        Number(form.lat) > 90)
    ) {
      setFormError("Latitude must be between -90 and 90.");
      return;
    }

    if (
      form.lng !== "" &&
      (Number.isNaN(Number(form.lng)) ||
        Number(form.lng) < -180 ||
        Number(form.lng) > 180)
    ) {
      setFormError("Longitude must be between -180 and 180.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      subDivisionId: form.subDivisionId.trim(),
      dspOfficerId: form.dspOfficerId || null,
      dspName: form.dspName,
      phone: form.phone.trim(),
      lat: form.lat === "" ? null : Number(form.lat),
      lng: form.lng === "" ? null : Number(form.lng),
      active: form.active,
    };

    try {
      if (editingId) {
        await onUpdateSubdivision(editingId, payload);
      } else {
        await onCreateSubdivision(payload);
      }

      closeModal();
    } catch (err) {
      setFormError(
        err?.message ||
        err ||
        `Unable to ${editingId ? "update" : "create"} sub-division.`
      );
    }
  };

  return (
    <>
      {/* HEADER */}
      <header className="bg-slate-900 border-b border-slate-800 px-4 py-2 flex items-center justify-between shrink-0 shadow-md h-16 relative z-30">
        {/* 1. OFFICIAL IDENTITY & BRANDING */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-700 via-indigo-600 to-blue-500 flex items-center justify-center text-white shadow-lg shadow-blue-900/50 text-xl font-black shrink-0 border border-blue-400/30">
            🇮🇳
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-sm font-black tracking-tight text-white flex items-center gap-2 uppercase">
                Nellore Police

              </h1>
            </div>

          </div>
        </div>
        {/* 2. ICONS-WISE PORTAL NAVIGATION BAR (Proper Website Style) */}
        <nav className="hidden lg:flex items-center bg-slate-950 p-1 rounded-xl border border-slate-800 text-xs font-semibold shadow-inner">
          <a href="/admin" className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition flex items-center gap-1.5">
            <span>
              🗺️
            </span>
            <span>
              Live Map
            </span>
          </a>
          <a href="/emergency-sos" className="px-3 py-1.5 text-slate-300 hover:text-white hover:bg-slate-900 rounded-lg transition flex items-center gap-1.5">
            <span>
              🚨
            </span>
            <span>
              Incident Grid
            </span>
          </a>


        </nav>
        {/* 3. ACTIONS, TOOLS DROPDOWN LIST, THEME & USER PROFILE */}
        <div className="flex items-center gap-2">
          {/* Register Officer Primary Quick Action */}

          {/* Upload Primary Quick Action */}
          <button onClick={(event) => { openUploadModal('MASTER') }} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg shadow flex items-center gap-1.5 transition">
            <span>
              📤
            </span>
            {' '}
            <span className="hidden sm:inline">
              Upload
            </span>
            {' '}Roster
          </button>
          {/* Operations & Tools Dropdown (Proper List-Wise View) */}
          <div className="relative inline-block text-left" id="header-tools-dropdown-container">


          </div>

          <ThemeSwitcher className="mr-0.5" labelClass="hidden 2xl:inline" />
          <div className="h-6 w-px bg-slate-800 mx-0.5"></div>
          {/* Authenticated Commander Profile & Logout */}
          <div className="flex items-center gap-2 pl-1">
            <div id="header-commander-insignia" className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black shadow border border-yellow-400/40 shrink-0" style={{ background: "#172554", color: "#fef08a" }}>
              🦁
            </div>
            <div className="hidden sm:block text-right">
              <div
                id="header-commander-name"
                className="text-xs font-black text-white leading-tight truncate max-w-[130px]"
              >
                {user?.displayName || user?.name || user?.username || "Admin"}
              </div>

              <div
                id="header-commander-scope"
                className="text-[10px] text-amber-300 font-bold leading-tight truncate max-w-[160px]"
              >
                👑 {user?.designation || user?.role || "Administrator"}
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              title="Logout"
              className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-red-900/40 text-slate-300 hover:text-red-300 border border-slate-700 flex items-center justify-center text-sm transition cursor-pointer"
            >
              🚪
            </button>
          </div>
        </div>
      </header>

      {/* TOP KPI METRIC STRIP (CLICK ANY CARD TO OPEN IN SEPARATE GRID VIEW) */}
      <div className="bg-slate-900/90 border-b border-slate-800/80 px-4 py-2 shrink-0 grid grid-cols-10 gap-2 text-xs">

        {/* Total Sub Divisions */}
        <div
          onClick={() => navigate("/sub-divisions")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-purple-400 transition flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] text-purple-400 uppercase font-bold tracking-wider">
              Sub Divisions
            </div>
            <div className="text-base font-black text-purple-300">
              {dashboardLoading ? "..." : dashboard?.totalSubDivisions ?? 0}
            </div>
          </div>
          <span className="text-xl text-purple-400 opacity-50">🏢</span>
        </div>

        {/* Total Circles */}
        <div
          onClick={() => navigate("/circles")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-sky-400 transition flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] text-sky-400 uppercase font-bold tracking-wider">
              Total Circles
            </div>
            <div className="text-base font-black text-sky-300">
              {dashboardLoading ? "..." : dashboard?.totalCircles ?? 0}
            </div>
          </div>
          <span className="text-xl text-sky-400 opacity-50">⭕</span>
        </div>

        {/* Total Police Stations */}
        <div
          onClick={() => navigate("/stations")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-teal-400 transition flex items-center justify-between"
        >
          <div>
            <div className="text-[10px] text-teal-400 uppercase font-bold tracking-wider">
              Police Stations
            </div>
            <div className="text-base font-black text-teal-300">
              {dashboardLoading ? "..." : dashboard?.totalStations ?? 0}
            </div>
          </div>
          <span className="text-xl text-teal-400 opacity-50">🚔</span>
        </div>
        <div onClick={() => navigate("/booths")}
          className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-blue-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
              Total Booths
            </div>
            <div id="stat-total-booths" className="text-base font-black text-white">
              {dashboardLoading ? "..." : dashboard?.totalPollingStations ?? 0}
            </div>
          </div>
          <span className="text-xl opacity-40">
            🗳️
          </span>
        </div>
        <div onClick={() => navigate("/officers")} className="cursor-pointer bg-slate-800/60 hover:bg-slate-800 rounded-lg p-2 border border-slate-700/60 hover:border-indigo-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-indigo-400 uppercase font-bold tracking-wider">
              Total Men
            </div>
            <div id="stat-total-staff" className="text-base font-black text-indigo-300">
              {dashboardLoading ? "..." : dashboard?.totalOfficers ?? 0}
            </div>
          </div>
          <span className="text-xl text-indigo-400 opacity-50">
            👮
          </span>
        </div>
        <div onClick={() => navigate("/booths")} className="cursor-pointer bg-rose-950/40 hover:bg-rose-900/40 rounded-lg p-2 border border-rose-600/40 hover:border-rose-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-rose-400 uppercase font-bold tracking-wider">
              Hyper Sensitive
            </div>
            <div id="stat-hyper-sensitive" className="text-base font-black text-rose-400">
              {dashboardLoading ? "..." : dashboard?.hyperSensitiveBooths ?? 0}
            </div>
          </div>
          <span className="text-xl text-rose-400">
            🔥
          </span>
        </div>
        <div onClick={() => navigate("/booths")} className="cursor-pointer bg-amber-950/30 hover:bg-amber-900/40 rounded-lg p-2 border border-amber-600/40 hover:border-amber-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-amber-400 uppercase font-bold tracking-wider">
              Sensitive
            </div>
            <div id="stat-hyper-sensitive" className="text-base font-black text-rose-400">
              {dashboardLoading ? "..." : dashboard?.hyperSensitiveBooths ?? 0}
            </div>
          </div>
          <span className="text-xl text-amber-400">
            ⚠️
          </span>
        </div>

        <div onClick={() => navigate("/staff/on-site")} title="View Verified On-Site Personnel in Grid View" className="cursor-pointer bg-emerald-950/20 hover:bg-emerald-900/30 rounded-lg p-2 border border-emerald-600/30 hover:border-emerald-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-emerald-400 uppercase font-bold tracking-wider">
              On-Site (≤75m)
            </div>
            <div id="stat-onsite" className="text-base font-black text-emerald-400">
              {dashboardLoading ? "..." : dashboard?.atPollingStation ?? 0}
            </div>
          </div>
          <span className="text-xl text-emerald-500 opacity-60">
            ✓
          </span>
        </div>
        <div id="card-kpi-absent" onClick={() => navigate("/staff/absent")} className="cursor-pointer bg-rose-950/30 hover:bg-rose-900/40 rounded-lg p-2 border border-rose-500/50 hover:border-rose-400 transition flex items-center justify-between">
          <div>
            <div className="text-[10px] text-rose-400 uppercase font-bold tracking-wider">
              Not Attended
            </div>
            <div id="stat-non-attended" className="text-base font-black text-rose-400">
              {dashboardLoading ? "..." : dashboard?.absentAttendance ?? 0}
            </div>
          </div>
          <span className="text-xl text-rose-500">
            ✕
          </span>
        </div>
        <div id="card-kpi-sos" onClick={() => navigate("/emergency-sos")} title="Click to View Active Emergency SOS Distress Incidents Directly" className="cursor-pointer bg-red-950/30 hover:bg-red-900/50 rounded-lg p-2 border border-red-500/50 hover:border-red-400 transition flex items-center justify-between animate-sos-strobe">
          <div>
            <div className="text-[10px] text-red-400 uppercase font-bold tracking-wider">
              Emergency SOS
            </div>
            <div id="stat-sos" className="text-base font-black text-red-500">
              {dashboardLoading ? "..." : dashboard?.activeSOS ?? 0}
            </div>
          </div>
          <span className="text-xl text-red-500">
            🚨
          </span>
        </div>
      </div >

      {/* PAGE TITLE */}
      <section className="px-5 py-5  border-slate-800 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-blue-400 font-bold mb-1">
                District Administration / Sub-Division Management
              </div>

              <h2 className="text-xl font-black text-white">
                🏛️ All Sub Divisions
              </h2>

              <p className="text-xs text-slate-400 mt-1">
                View and manage all registered police sub-divisions.
              </p>
            </div>

            <button
              type="button"
              onClick={openAddModal}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700"
            >
              <i className="fa-solid fa-plus" />
              Add Sub Division
            </button>
          </div>
        </div>
      </section>

      {/* STATISTICS */}
      <section className="px-5 py-4 border-slate-800">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-3">
            <p className="text-[10px] uppercase text-slate-400 font-bold">
              Total Sub Divisions
            </p>
            <h3 className="text-xl font-black text-white mt-1">
              {statistics.total || 0}
            </h3>
          </div>

          <div className="bg-emerald-950/30 border border-emerald-700/40 rounded-xl p-3">
            <p className="text-[10px] uppercase text-emerald-400 font-bold">
              Active
            </p>
            <h3 className="text-xl font-black text-emerald-400 mt-1">
              {statistics.active || 0}
            </h3>
          </div>

          <div className="bg-rose-950/30 border border-rose-700/40 rounded-xl p-3">
            <p className="text-[10px] uppercase text-rose-400 font-bold">
              Inactive
            </p>
            <h3 className="text-xl font-black text-rose-400 mt-1">
              {statistics.inactive || 0}
            </h3>
          </div>

          <div className="bg-indigo-950/30 border border-indigo-700/40 rounded-xl p-3">
            <p className="text-[10px] uppercase text-indigo-400 font-bold">
              DSP Assigned
            </p>
            <h3 className="text-xl font-black text-indigo-400 mt-1">
              {statistics.withDsp || 0}
            </h3>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="px-5 py-4  border-slate-800 bg-slate-950">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-2.5 text-slate-500">
              🔍
            </span>

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sub-division name, ID, DSP name or phone..."
              className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-9 pr-3 py-2 text-xs text-white outline-none focus:border-blue-500"
            />
          </div>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white outline-none"
          >
            <option value="ALL">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>

          <button
            type="button"
            onClick={() => {
              setSearch("");
              setStatus("ALL");
            }}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-xl text-xs font-bold text-slate-300"
          >
            Reset Filters
          </button>

          <div className="flex items-center bg-slate-900 p-1 rounded-xl border border-slate-700">
            <button
              type="button"
              onClick={() => setViewMode("GRID")}
              className={`px-3 py-2 rounded-lg text-xs font-bold transition ${viewMode === "GRID"
                ? "bg-blue-600 text-white"
                : "text-slate-400 hover:text-white"
                }`}
            >
              ▦ Grid
            </button>

            <button
              type="button"
              onClick={() => setViewMode("LIST")}
              className={`px-3 py-2 rounded-lg text-xs font-bold transition ${viewMode === "LIST"
                ? "bg-blue-600 text-white"
                : "text-slate-400 hover:text-white"
                }`}
            >
              ☷ List
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-3 text-[10px] text-slate-500">
          Showing{" "}
          <span className="text-white font-bold">
            {subdivisions.length}
          </span>{" "}
          of {totalSubDivisions} sub-divisions
        </div>
      </section>

      {/* SUB-DIVISION CONTENT */}
      <main className="flex-1 p-5 bg-slate-950">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20 text-slate-400 text-sm">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
              Loading sub-divisions...
            </div>
          ) : error ? (
            <div className="text-center py-16 bg-red-950/20 border border-red-800/40 rounded-2xl">
              <p className="text-red-400 text-sm font-bold">
                Failed to load sub-divisions
              </p>
              <p className="text-xs text-slate-400 mt-2">
                {typeof error === "string"
                  ? error
                  : error?.message || "Something went wrong"}
              </p>
            </div>
          ) : subdivisions.length === 0 ? (
            <div className="text-center py-20 bg-slate-900 border border-slate-800 rounded-2xl">
              <div className="text-4xl mb-3">🔍</div>
              <h3 className="text-sm font-bold text-white">
                No Sub-Divisions Found
              </h3>
              <p className="text-xs text-slate-500 mt-2">
                Try changing your search or filters.
              </p>
            </div>
          ) : viewMode === "GRID" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {subdivisions.map((subdivision) => (
                <div
                  key={subdivision._id}
                  className="bg-slate-900 border border-slate-800 rounded-2xl p-4 hover:border-blue-500/60 transition shadow-lg"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-sm font-black text-white">
                        {subdivision.name || "Unnamed Sub-Division"}
                      </h3>

                      <p className="text-[11px] text-blue-400 font-bold mt-1">
                        {subdivision.subDivisionId || "--"}
                      </p>
                    </div>

                    <span
                      className={`text-[9px] px-2 py-1 rounded-lg border font-black ${subdivision.active
                        ? "bg-emerald-950 text-emerald-400 border-emerald-800"
                        : "bg-red-950 text-red-400 border-red-800"
                        }`}
                    >
                      {subdivision.active ? "ACTIVE" : "INACTIVE"}
                    </span>
                  </div>

                  <div className="mt-4 space-y-3 text-xs">
                    <div className="flex justify-between gap-3">
                      <span className="text-slate-500">Sub-Division ID</span>
                      <span className="text-slate-200 font-semibold">
                        {subdivision.subDivisionId || "--"}
                      </span>
                    </div>

                    <div className="flex justify-between gap-3">
                      <span className="text-slate-500">DSP Officer</span>
                      <span className="text-slate-200 font-semibold">
                        {subdivision.dspName || "Not Assigned"}
                      </span>
                    </div>

                    <div className="flex justify-between gap-3">
                      <span className="text-slate-500">Phone</span>
                      <span className="text-slate-200">
                        {subdivision.phone || "--"}
                      </span>
                    </div>



                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between">
                    <span className="text-[10px] text-slate-500">
                      DSP Assignment
                    </span>

                    <span
                      className={`text-[10px] font-bold ${subdivision.dspOfficerId || subdivision.dspName
                        ? "text-emerald-400"
                        : "text-amber-400"
                        }`}
                    >
                      {subdivision.dspOfficerId || subdivision.dspName
                        ? "Assigned"
                        : "Pending"}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => openEditModal(subdivision._id)}
                    className="w-full mt-4 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition"
                  >
                    ✏️ Edit Sub-Division
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto bg-slate-900 border border-slate-800 rounded-2xl shadow-xl">
              <table className="w-full text-left text-xs text-slate-300">
                <thead className="bg-slate-950 text-slate-400 uppercase text-[10px] font-bold border-b border-slate-800">
                  <tr>
                    <th className="p-4">Sub-Division</th>
                    <th className="p-4">Sub-Division ID</th>
                    <th className="p-4">DSP Officer</th>
                    <th className="p-4">Phone</th>
                    <th className="p-4">Coordinates</th>
                    <th className="p-4">Created On</th>
                    <th className="p-4">Status</th>
                    <th className="p-4 text-right">Action</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-800">
                  {subdivisions.map((subdivision) => (
                    <tr
                      key={subdivision._id}
                      className="hover:bg-slate-800/60 transition"
                    >
                      <td className="p-4 font-bold text-white">
                        {subdivision.name || "--"}
                      </td>

                      <td className="p-4 text-blue-400 font-bold">
                        {subdivision.subDivisionId || "--"}
                      </td>

                      <td className="p-4">
                        {subdivision.dspName || "Not Assigned"}
                      </td>

                      <td className="p-4">
                        {subdivision.phone || "--"}
                      </td>

                      <td className="p-4 text-cyan-400 font-mono text-[10px]">
                        {formatCoordinates(
                          subdivision.lat,
                          subdivision.lng
                        )}
                      </td>

                      <td className="p-4 whitespace-nowrap">
                        {subdivision.createdAt
                          ? new Date(
                            subdivision.createdAt
                          ).toLocaleDateString("en-IN")
                          : "--"}
                      </td>

                      <td className="p-4">
                        <span
                          className={`px-2 py-1 rounded-lg text-[10px] font-bold ${subdivision.active
                            ? "bg-emerald-950 text-emerald-400"
                            : "bg-red-950 text-red-400"
                            }`}
                        >
                          {subdivision.active ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>

                      <td className="p-4 text-right">
                        <button
                          type="button"
                          onClick={() => openEditModal(subdivision._id)}
                          className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-bold transition whitespace-nowrap"
                        >
                          ✏️ Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* FOOTER */}
      <footer className="px-5 py-3 border-t border-slate-800 bg-slate-900 text-center">
        <p className="text-[10px] text-slate-500">
          Nellore Police • Sub-Division Administration
        </p>
      </footer>

      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div
          className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="w-full max-w-xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
            {/* MODAL HEADER */}
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black text-white">
                  {editingId ? "Edit Sub-Division" : "Add Sub-Division"}
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  {editingId
                    ? "Update the sub-division information."
                    : "Enter the new sub-division information."}
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xl disabled:opacity-50"
              >
                ×
              </button>
            </div>

            {/* MODAL BODY */}
            {detailsLoading && editingId ? (
              <div className="py-16 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
                <p className="text-sm text-slate-400">
                  Loading subdivision details...
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="p-6 space-y-4 max-h-[65vh] overflow-y-auto">
                  {formError && (
                    <div className="p-3 rounded-lg bg-red-950/40 border border-red-800 text-red-400 text-xs">
                      {typeof formError === "string"
                        ? formError
                        : formError?.message || "Something went wrong."}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-2">
                      Sub-Division Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => updateField("name", e.target.value)}
                      placeholder="Enter sub-division name"
                      required
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-2">
                      Sub-Division ID <span className="text-red-400">*</span>
                    </label>

                    <input
                      type="text"
                      value={form.subDivisionId}
                      onChange={(e) =>
                        updateField("subDivisionId", e.target.value.toUpperCase())
                      }
                      placeholder="Enter Sub-Division ID (e.g. SD001)"
                      required
                      maxLength={20}
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-2">
                      DSP Officer
                    </label>

                    <select
                      value={form.dspOfficerId || ""}
                      onChange={(e) => {
                        const selected = dspUsers.find(
                          (user) => user._id === e.target.value
                        );

                        setForm((previous) => ({
                          ...previous,
                          dspOfficerId: e.target.value,
                          dspName: selected
                            ? selected.displayName || selected.username || ""
                            : "",
                        }));
                      }}
                      disabled={usersLoading}
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-blue-500"
                    >
                      <option value="">
                        {usersLoading
                          ? "Loading DSP officers..."
                          : dspUsers.length === 0
                            ? "No DSP officers available"
                            : "Select DSP Officer"}
                      </option>

                      {dspUsers.map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.displayName || user.username || "Unnamed Officer"}
                          {user.officerId ? ` (${user.officerId})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]{10}"
                      maxLength={10}
                      value={form.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                        updateField("phone", value);
                      }}
                      placeholder="Enter 10-digit phone number"
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-blue-500"
                    />
                  </div>



                  <div className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-xl p-4">
                    <div>
                      <p className="text-sm font-bold text-white">
                        Sub-Division Status
                      </p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Enable or disable this sub-division.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => updateField("active", !form.active)}
                      className={`relative w-12 h-6 rounded-full transition ${form.active ? "bg-emerald-600" : "bg-slate-700"
                        }`}
                      aria-label="Toggle sub-division status"
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${form.active ? "left-7" : "left-1"
                          }`}
                      />
                    </button>
                  </div>

                  <p
                    className={`text-xs font-bold ${form.active ? "text-emerald-400" : "text-red-400"
                      }`}
                  >
                    {form.active ? "ACTIVE" : "INACTIVE"}
                  </p>
                </div>

                {/* MODAL FOOTER */}
                <div className="px-6 py-4 border-t border-slate-800 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    disabled={saving}
                    className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold disabled:opacity-50"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={saving || (detailsLoading && !!editingId)}
                    className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving && (
                      <span className="animate-spin h-4 w-4 border-2 border-white/40 border-t-white rounded-full" />
                    )}

                    {saving
                      ? "Saving..."
                      : editingId
                        ? "Update Sub-Division"
                        : "Create Sub-Division"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}